import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  List,
  Pencil,
  Table2,
  Trash2,
  X,
} from "lucide-react";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfISOWeek,
  endOfYear,
  format,
  getISOWeek,
  startOfISOWeek,
  startOfYear,
} from "date-fns";
import { nl } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import { dayTooltip, MonthCalendar, PlannerLegend } from "../components/MonthCalendar";
import PageHeader from "../components/PageHeader";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  BalanceMutation,
  BalanceSummary,
  CalendarDay,
  CalendarResponse,
  LeaveLayer,
  Paginated,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate, formatMinutes, organizationQuery } from "../utils/format";

type ViewMode = "calendar" | "table";
type LayerKey = "school_holidays" | "public_holidays" | "leave" | "events" | "notes";

const layerOptions: Array<{ key: LayerKey; label: string }> = [
  { key: "leave", label: "Verlof" },
  { key: "school_holidays", label: "Schoolvakanties en studiedagen" },
  { key: "public_holidays", label: "Feestdagen" },
  { key: "events", label: "Verjaardagen en afspraken" },
  { key: "notes", label: "Notities" },
];

export default function PlannerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organizationId, organization } = useWorkspace();
  const yearParam = Number(searchParams.get("year"));
  const [year, setYear] = useState(
    Number.isInteger(yearParam) && yearParam > 0 ? yearParam : new Date().getFullYear(),
  );
  const viewParam = searchParams.get("view");
  const [view, setView] = useState<ViewMode>(viewParam === "table" ? "table" : "calendar");
  const [layers, setLayers] = useState<Set<LayerKey>>(
    new Set(layerOptions.map((option) => option.key)),
  );
  const [showBalanceColumn, setShowBalanceColumn] = useState(false);
  const [openingMinutes, setOpeningMinutes] = useState(0);
  const [mutations, setMutations] = useState<BalanceMutation[]>([]);
  const [calendar, setCalendar] = useState<CalendarResponse | null>(null);
  const [selection, setSelection] = useState<{ start: string; end: string } | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    const organizationPart = organizationQuery(organizationId);
    const layerPart = [...layers].join(",");
    const query = [
      "granularity=year",
      `date=${year}-01-01`,
      `layers=${layerPart}`,
      organizationPart,
    ]
      .filter(Boolean)
      .join("&");
    setLoading(true);
    setError("");
    void api
      .get<CalendarResponse>(`/leave/calendar/overview/?${query}`)
      .then((response) => setCalendar(response.data))
      .catch(() => setError("De jaarplanner kon niet worden geladen."))
      .finally(() => setLoading(false));
  }, [year, organizationId, layers, reloadToken]);

  useEffect(() => {
    if (!showBalanceColumn) return;
    const organizationPart = organizationQuery(organizationId);
    const overviewQuery = ["period=calendar", `year=${year}`, organizationPart]
      .filter(Boolean)
      .join("&");
    void api
      .get<{ summary: BalanceSummary[] }>(`/leave/balances/overview/?${overviewQuery}`)
      .then((response) =>
        setOpeningMinutes(
          response.data.summary.reduce((sum, row) => sum + (row.opening_minutes ?? 0), 0),
        ),
      )
      .catch(() => setOpeningMinutes(0));
    const mutationsQuery = [
      `date_from=${year}-01-01`,
      `date_to=${year}-12-31`,
      "page_size=100",
      organizationPart,
    ]
      .filter(Boolean)
      .join("&");
    void api
      .get<Paginated<BalanceMutation> | BalanceMutation[]>(
        `/leave/mutations/?${mutationsQuery}`,
      )
      .then((response) => setMutations(unwrapResults(response.data)))
      .catch(() => setMutations([]));
  }, [showBalanceColumn, year, organizationId]);

  const dayMap = useMemo(
    () => new Map(calendar?.days.map((day) => [day.date, day]) ?? []),
    [calendar],
  );
  const months = eachMonthOfInterval({
    start: startOfYear(new Date(year, 0, 1)),
    end: endOfYear(new Date(year, 0, 1)),
  });
  const selectDate = (date: string, extend: boolean) => {
    if (extend && selection) {
      setSelection({
        start: selection.start < date ? selection.start : date,
        end: selection.start < date ? date : selection.start,
      });
      return;
    }
    setSelection({ start: date, end: date });
  };
  const planSelection = () => {
    if (!selection) return;
    const params = new URLSearchParams({
      start_date: selection.start,
      end_date: selection.end,
      return_to: `/app/planner?view=${view}&year=${year}`,
    });
    navigate(`/app/requests/new?${params.toString()}`);
  };

  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const returnTo = `/app/planner?view=${view}&year=${year}`;
  // Verlofregels binnen de selectie, ontdubbeld: een meerdaagse aanvraag staat
  // op elke dag in de kalenderlagen.
  const selectedLeave = useMemo(() => {
    if (!selection) return [];
    const found = new Map<number, { row: LeaveLayer; date: string }>();
    for (const [iso, day] of dayMap) {
      if (iso < selection.start || iso > selection.end) continue;
      for (const row of day.layers.leave ?? []) {
        if (!found.has(row.id)) found.set(row.id, { row, date: iso });
      }
    }
    return [...found.values()];
  }, [dayMap, selection]);

  const removeLeave = async (entryId: number) => {
    if (
      !window.confirm(
        "Dit verlof definitief verwijderen? Goedgekeurde uren komen terug op je saldo.",
      )
    ) {
      return;
    }
    setDeletingId(entryId);
    setError("");
    try {
      await api.delete(`/leave/entries/${entryId}/`);
      setSuccess("Het verlof is verwijderd.");
      setReloadToken((value) => value + 1);
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "Het verlof kon niet worden verwijderd."));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="planner-page">
      <PageHeader
        eyebrow={organization?.name ?? "Persoonlijke planning"}
        title="Jaarplanner"
        description={
          calendar?.has_personal_schools
            ? "Plan rondom feestdagen en de schoolvakanties van je scholen."
            : "Plan rondom feestdagen. Voeg onder Instellingen een school toe om schoolvakanties te zien."
        }
        actions={
          <button
            className="button primary"
            onClick={() =>
              navigate(
                `/app/requests/new?${new URLSearchParams({
                  return_to: `/app/planner?view=${view}&year=${year}`,
                }).toString()}`,
              )
            }
          >
            <CalendarDays size={18} /> Nieuw verlof
          </button>
        }
      />

      <div className="planner-toolbar">
        <div className="segmented-control" aria-label="Plannerweergave">
          <button
            className={view === "calendar" ? "active" : ""}
            onClick={() => setView("calendar")}
          >
            <List size={17} /> Kalender
          </button>
          <button
            className={view === "table" ? "active" : ""}
            onClick={() => setView("table")}
          >
            <Table2 size={17} /> Tabel
          </button>
        </div>
        <div className="year-stepper">
          <button
            className="icon-button"
            title="Vorig jaar"
            aria-label="Vorig jaar"
            onClick={() => setYear((value) => value - 1)}
          >
            <ChevronLeft size={18} />
          </button>
          <strong>{year}</strong>
          <button
            className="icon-button"
            title="Volgend jaar"
            aria-label="Volgend jaar"
            onClick={() => setYear((value) => value + 1)}
          >
            <ChevronRight size={18} />
          </button>
          <button
            className="button subtle"
            onClick={() => setYear(new Date().getFullYear())}
          >
            Vandaag
          </button>
        </div>
        <details className="filter-menu">
          <summary>
            <Filter size={17} /> Lagen
          </summary>
          <div className="filter-popover">
            {layerOptions.map((option) => (
              <label key={option.key}>
                <input
                  type="checkbox"
                  checked={layers.has(option.key)}
                  onChange={() =>
                    setLayers((current) => {
                      const next = new Set(current);
                      if (next.has(option.key)) next.delete(option.key);
                      else next.add(option.key);
                      return next;
                    })
                  }
                />
                {option.label}
              </label>
            ))}
            {view === "table" && (
              <label>
                <input
                  type="checkbox"
                  checked={showBalanceColumn}
                  onChange={() => setShowBalanceColumn((current) => !current)}
                />
                Lopend saldo per week
              </label>
            )}
          </div>
        </details>
        {selection && (
          <div className="planner-selection-actions">
            <span>
              {selection.start === selection.end
                ? "1 datum geselecteerd"
                : `${selection.start} t/m ${selection.end}`}
            </span>
            <button className="button small primary" onClick={planSelection}>
              <CalendarDays size={16} /> Verlof plannen
            </button>
            <button
              className="icon-button"
              title="Selectie wissen"
              aria-label="Selectie wissen"
              onClick={() => setSelection(null)}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {selection && selectedLeave.length > 0 && (
        <section className="surface planner-selection-panel">
          <div className="settings-heading">
            <h2>Verlof in de selectie</h2>
            <p>Wijzig of verwijder verlof zonder de jaarplanner te verlaten.</p>
          </div>
          <div className="planner-selection-list">
            {selectedLeave.map(({ row, date }) => (
              <article key={row.id}>
                <span>
                  <strong>{row.leave_type.name}</strong>
                  <small>
                    {formatDate(date)} · {formatMinutes(row.requested_minutes)} ·{" "}
                    {row.status_label}
                  </small>
                </span>
                <span className="row-actions">
                  <button
                    className="icon-button"
                    title="Verlof wijzigen"
                    aria-label={`${row.leave_type.name} wijzigen`}
                    onClick={() =>
                      navigate(
                        `/app/requests/${row.id}/edit?${new URLSearchParams({
                          return_to: returnTo,
                        }).toString()}`,
                      )
                    }
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="icon-button danger"
                    title="Verlof verwijderen"
                    aria-label={`${row.leave_type.name} verwijderen`}
                    disabled={deletingId === row.id}
                    onClick={() => void removeLeave(row.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              </article>
            ))}
          </div>
        </section>
      )}

      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      {loading && <LoadingState label="Jaar laden..." />}

      {!loading && view === "calendar" && (
        <section className="year-calendar" aria-label={`Jaarplanner ${year}`}>
          {months.map((month) => (
            <MonthCalendar
              key={month.toISOString()}
              month={month}
              dayMap={dayMap}
              selection={selection}
              onSelect={selectDate}
              todayIso={todayIso}
            />
          ))}
        </section>
      )}

      {!loading && view === "table" && (
        <>
          {showBalanceColumn && (
            <p className="planner-opening-balance">
              Beginsaldo {year}: <strong>{formatMinutes(openingMinutes)}</strong>
            </p>
          )}
          <PlannerTable
            year={year}
            dayMap={dayMap}
            selection={selection}
            onSelect={selectDate}
            todayIso={todayIso}
            showBalanceColumn={showBalanceColumn}
            openingMinutes={openingMinutes}
            mutations={mutations}
          />
          <section className="planner-key" aria-label="Uitleg jaarplanner">
            <span className="planner-key-item school">
              <i>12</i>
              <span>
                <strong>Cirkel = schoolvakantie / studiedag</strong>
                <small>Alleen de cirkel is actief.</small>
              </span>
            </span>
            <span className="planner-key-item leave">
              <i>12</i>
              <span>
                <strong>Gekleurd blok = verlofstatus</strong>
                <small>Toont de status van je verlof.</small>
              </span>
            </span>
            <span className="planner-key-item combined">
              <i>12</i>
              <span>
                <strong>Combinatie = vakantie en verlof vallen samen</strong>
                <small>Vakantie en verlof zijn beide zichtbaar.</small>
              </span>
            </span>
          </section>
        </>
      )}

      <PlannerLegend />
      <p className="planner-legend-note">
        Verlof kleurt de dag; schooldagen krijgen een ring zodat informatie naast elkaar
        zichtbaar blijft.
      </p>
    </div>
  );
}

function PlannerTable({
  year,
  dayMap,
  selection,
  onSelect,
  todayIso,
  showBalanceColumn,
  openingMinutes,
  mutations,
}: {
  year: number;
  dayMap: Map<string, CalendarDay>;
  selection: { start: string; end: string } | null;
  onSelect: (date: string, extend: boolean) => void;
  todayIso: string;
  showBalanceColumn: boolean;
  openingMinutes: number;
  mutations: BalanceMutation[];
}) {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  const weeks = eachWeekOfInterval(
    { start: yearStart, end: yearEnd },
    { weekStartsOn: 1 },
  );
  let runningBalance = openingMinutes;
  return (
    <div className="surface planner-table-wrap">
      <table className="data-table planner-table">
        <thead>
          <tr>
            <th>Week</th>
            <th>Van</th>
            <th>Tot</th>
            {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((day) => (
              <th key={day}>{day}</th>
            ))}
            <th>Mutatie</th>
            {showBalanceColumn && <th>Saldo</th>}
            <th>Status</th>
            <th>Opmerking</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((weekStart) => {
            const start = startOfISOWeek(weekStart);
            const end = endOfISOWeek(weekStart);
            const weekDays = eachDayOfInterval({ start, end });
            const calendarDays = weekDays.map((date) =>
              dayMap.get(format(date, "yyyy-MM-dd")),
            );
            const leaveRows = calendarDays.flatMap((day) => day?.layers.leave ?? []);
            const mutation = leaveRows.reduce(
              (sum, row) => sum - row.requested_minutes,
              0,
            );
            const statuses = [...new Set(leaveRows.map((row) => row.status_label))];
            const notes = calendarDays.flatMap(
              (day) => day?.layers.notes?.map((note) => note.text) ?? [],
            );
            if (showBalanceColumn) {
              const startIso = format(start, "yyyy-MM-dd");
              const endIso = format(end, "yyyy-MM-dd");
              runningBalance += mutations
                .filter((row) => row.date && row.date >= startIso && row.date <= endIso)
                .reduce((sum, row) => sum + row.minutes_delta, 0);
            }
            const weekBalance = runningBalance;
            return (
              <tr key={start.toISOString()}>
                <td>
                  <strong>{getISOWeek(start)}</strong>
                </td>
                <td>{format(start, "dd-MM")}</td>
                <td>{format(end, "dd-MM")}</td>
                {weekDays.map((date, index) => {
                  const iso = format(date, "yyyy-MM-dd");
                  const day = calendarDays[index];
                  const leaveStatus = day?.layers.leave?.[0]?.status;
                  const previousDay = dayMap.get(
                    format(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1), "yyyy-MM-dd"),
                  );
                  const nextDay = dayMap.get(
                    format(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1), "yyyy-MM-dd"),
                  );
                  const continuesFromPrevious =
                    leaveStatus === previousDay?.layers.leave?.[0]?.status;
                  const continuesToNext = leaveStatus === nextDay?.layers.leave?.[0]?.status;
                  const hasSchoolHoliday = Boolean(day?.layers.school_holidays?.length);
                  const hasBirthday = Boolean(
                    day?.layers.events?.some((event) => event.event_type === "birthday"),
                  );
                  return (
                    <td
                      key={iso}
                      className={`planner-day-cell ${
                        day?.flags.is_public_holiday ? " holiday-cell" : ""
                      } ${
                        hasSchoolHoliday ? " school-cell" : ""
                      } ${
                        leaveStatus ? `leave-status-${leaveStatus}` : ""
                      } ${
                        leaveStatus && !continuesFromPrevious ? "leave-run-start" : ""
                      } ${
                        leaveStatus && !continuesToNext ? "leave-run-end" : ""
                      } ${
                        selection && iso >= selection.start && iso <= selection.end
                          ? " selected-cell"
                          : ""
                      } ${iso === todayIso ? " is-today" : ""}`}
                    >
                      <button
                        title={dayTooltip(day)}
                        aria-label={`${format(date, "d MMMM yyyy", { locale: nl })}: ${dayTooltip(day)}`}
                        onClick={(event) => onSelect(iso, event.shiftKey)}
                      >
                        <span className="planner-day-date">{format(date, "d")}</span>
                        <small className="planner-day-hours">
                          {day?.scheduled_minutes
                            ? `${formatMinutes(day.scheduled_minutes)}`
                            : "-"}
                        </small>
                        {day?.schedule_source ? (
                          <small className="planner-day-source">
                            Rooster: {day.scheduled_minutes / 60}
                          </small>
                        ) : null}
                        {hasBirthday ? <i className="event-dot birthday" /> : null}
                        {day?.flags.is_public_holiday ? <i className="holiday-dot" /> : null}
                      </button>
                      {day?.employment_schedules?.length ? (
                        <small className="employment-hours">
                          {day.employment_schedules
                            .map(
                              (item) =>
                                `${item.employment_name}: ${
                                  item.scheduled_minutes / 60
                                }`,
                            )
                            .join(" · ")}
                        </small>
                      ) : null}
                    </td>
                  );
                })}
                <td className={mutation < 0 ? "negative" : ""}>
                  {mutation ? formatMinutes(mutation) : "-"}
                </td>
                {showBalanceColumn && (
                  <td className={weekBalance < 0 ? "negative" : ""}>
                    {formatMinutes(weekBalance)}
                  </td>
                )}
                <td>
                  {statuses.length ? (
                    <span
                      className={`planner-status-chip status-${leaveRows[0].status}`}
                    >
                      {statuses.join(", ")}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="truncate-cell" title={notes.join(" · ")}>
                  {notes.join(" · ") || "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

